import React, { createContext, useContext, useState } from "react";
import styles from "./pricing-cards.module.scss";
import classnames from "classnames";
import List from "../List/list";
import SbEditable from "storyblok-react";
import Button from "@/components/Button/button";
import { RadioInput } from "@/components/Form/inputs";
import { PricingCardsExtendedStoryblok } from "@/types/component-types-sb";

const defaultValue = {
  monthly: true,
  setMonthly: (value: boolean) => {},
};

const PriceContext = createContext(defaultValue);

interface Props {
  blok: PricingCardsExtendedStoryblok;
}

const PricingCardsExtended = (props: Props) => {
  const {
    blok: {
      core_title,
      core_copy,
      core_list,
      core_price,
      core_price_yearly,
      core_cta_text,
      core_cta_month,
      core_cta_year,
      premium_title,
      premium_copy,
      premium_list,
      premium_price,
      premium_price_yearly,
      premium_cta_text,
      premium_cta_month,
      premium_cta_year,
      elite_title,
      elite_copy,
      elite_list,
      elite_price,
      elite_per,
      elite_cta_text,
      elite_price_yearly,
      elite_cta_month,
      elite_cta_year,
    },
  } = props;

  return (
    // @ts-expect-error
    <SbEditable content={props.blok}>
      <div className={styles.pricing}>
        <PricingCardExtended
          type={"elite"}
          title={elite_title}
          copy={elite_copy}
          list={elite_list}
          price={elite_price}
          price_yearly={elite_price_yearly}
          per={elite_per}
          cta_text={elite_cta_text}
          cta_mth={elite_cta_month}
          cta_year={elite_cta_year}
        />
        <PricingCardExtended
          type={"core"}
          title={core_title}
          copy={core_copy}
          list={core_list}
          price={core_price}
          price_yearly={core_price_yearly}
          cta_text={core_cta_text}
          cta_mth={core_cta_month}
          cta_year={core_cta_year}
        />
        <PricingCardExtended
          type={"premium"}
          title={premium_title}
          copy={premium_copy}
          list={premium_list}
          price={premium_price}
          price_yearly={premium_price_yearly}
          cta_text={premium_cta_text}
          cta_mth={premium_cta_month}
          cta_year={premium_cta_year}
        />
      </div>
    </SbEditable>
  );
};

interface PricingCardExtendedProps {
  type?: string;
  title?: string;
  copy?: string;
  list?: any;
  price?: string;
  price_yearly?: string;
  per?: string;
  cta?: string;
  cta_text?: string;
  cta_mth?: string;
  cta_year?: string;
  className?: string;
}

const PricingCardExtended = (props: PricingCardExtendedProps) => {
  const [monthly, setMonthly] = useState(defaultValue.monthly);
  const {
    type,
    title = "Title",
    copy = "copy",
    price,
    price_yearly,
    list,
    cta_text,
    cta_mth,
    cta_year,
    cta,
    className,
    ...rest
  } = props;
  const classes = classnames(
    styles.card,
    styles.extended,
    styles[type || ""],
    className
  );

  const paymentGroup = (type: string) => {
    switch (type) {
      case "elite":
        return (
          <>
            <PaymentGroup
              cta_text={cta_text}
              cta_year={cta_year}
              cta_mth={cta_mth}
              style={{ ["--boolean-check-color" as string]: "black" }}
              buttonStyles={{
                "--btn-bg": "white",
                "--btn-color": "#191919",
                "--btn-accent": "#191919",
                "--btn-icon-color": "white",
              }}
            />
          </>
        );
      case "premium":
        return (
          <PaymentGroup
            cta_text={cta_text}
            cta_year={cta_year}
            cta_mth={cta_mth}
            style={{ ["--boolean-check-color" as string]: "var(--color-pink)" }}
            buttonStyles={{
              "--btn-color": "#191919",
              "--btn-icon-color": "white",
              "--btn-bg": "white",
              "--btn-accent": "var(--color-pink)",
            }}
          />
        );
      case "core":
        return (
          <PaymentGroup
            cta_text={cta_text}
            cta_year={cta_year}
            cta_mth={cta_mth}
            style={{ ["--boolean-bg-color" as string]: "#C4C4C4" }}
          />
        );
      default:
        return null;
    }
  };

  const pricePer = (monthly: boolean) => {
    return monthly ? "/month (charged monthly)" : "/month (charged yearly)";
  };

  const priceText = (monthly: boolean) => {
    return monthly ? price : price_yearly;
  };

  return (
    <PriceContext.Provider value={{ monthly, setMonthly }}>
      <div className={classes} {...rest}>
        <h4 className={styles.title}>{title}</h4>
        <hr className={styles.cardDivider} />
        <h6 className={styles.price}>{priceText(monthly)}</h6>
        <p className={styles.per}>{pricePer(monthly)}</p>
        <hr className={styles.cardDivider} />
        <h6 className={styles.copy}>{copy}</h6>
        <hr className={styles.cardDivider} />
        {list ? (
          <List
            blok={{ items: list, _uid: "", component: "list" }}
            className={styles.list}
          />
        ) : null}
        {type ? paymentGroup(type) : null}
      </div>
    </PriceContext.Provider>
  );
};

interface PaymentGroupProps extends React.ComponentPropsWithoutRef<"div"> {
  cta_mth?: string;
  cta_year?: string;
  cta_text?: string;
  type?: string;
  buttonStyles?: any;
}

const PaymentGroup = ({
  cta_mth,
  cta_year,
  cta_text,
  type,
  buttonStyles,
  ...rest
}: PaymentGroupProps) => {
  const { setMonthly } = useContext(PriceContext);
  const [selectedValue, setSelectedValue] = useState(cta_mth);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
    setMonthly(selectedValue !== cta_mth);
  };
  return (
    <div className={styles.paymentGroupWrapper} {...rest}>
      <hr className={classnames(styles.cardDivider)} />
      <div className={styles.paymentGroup}>
        <RadioInput
          value={cta_mth}
          checked={selectedValue === cta_mth}
          onChange={handleChange}
        >
          Pay monthly
        </RadioInput>
        <RadioInput
          value={cta_year}
          checked={selectedValue === cta_year}
          onChange={handleChange}
        >
          Pay yearly
        </RadioInput>
      </div>
      <div>
        <Button
          link={selectedValue}
          icon={"IconArrowRight"}
          style={buttonStyles}
        >
          {cta_text}
        </Button>
      </div>
    </div>
  );
};

export default PricingCardsExtended;
